import { agents } from "../data/agents";
import { apps } from "../data/apps";
import { components } from "../data/components";
import {
  APP_ANGLES,
  B2PROC_ANGLE,
  LAYER_RADII,
  posAtAngle,
  posOnCircle
} from "../hooks/useActiveNode";
import type { ActiveNode } from "../types/platform";

const ORANGE = "#F28132";
const PUSH_DISTANCE = 13;
const STAGGER_MS = 35;

type Pt = { x: number; y: number };
type RelationTarget = { name: string; tag: "primary" | "secondary" | "consumption" };
type Edge = { from: Pt; to: Pt; key: string };

function nodePosByName(name: string): Pt | null {
  const aIdx = agents.findIndex((a) => a.name === name);
  if (aIdx >= 0) return posOnCircle(aIdx, agents.length, LAYER_RADII.agent);

  if (Object.prototype.hasOwnProperty.call(APP_ANGLES, name)) {
    return posAtAngle(APP_ANGLES[name]!, LAYER_RADII.app);
  }

  const app = apps.find((a) => a.name === name);
  if (app && app.category === "b2proc") {
    return posAtAngle(B2PROC_ANGLE, LAYER_RADII.b2proc);
  }

  return null;
}

function bezierPath(from: Pt, to: Pt): string {
  const mx = (from.x + to.x) / 2;
  const my = (from.y + to.y) / 2;
  const vx = mx - 50;
  const vy = my - 50;
  const dist = Math.sqrt(vx * vx + vy * vy);

  let pushX: number;
  let pushY: number;
  if (dist > 0.01) {
    pushX = (vx / dist) * PUSH_DISTANCE;
    pushY = (vy / dist) * PUSH_DISTANCE;
  } else {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const chordLen = Math.sqrt(dx * dx + dy * dy) || 1;
    pushX = (-dy / chordLen) * PUSH_DISTANCE;
    pushY = (dx / chordLen) * PUSH_DISTANCE;
  }

  const cx = mx + pushX;
  const cy = my + pushY;
  return `M ${from.x} ${from.y} Q ${cx} ${cy} ${to.x} ${to.y}`;
}

export function getRelationTargets(
  active: ActiveNode
): readonly RelationTarget[] {
  if (!active) return [];
  const sourceName = active.data.name;
  const targets: RelationTarget[] = [];

  if (active.kind === "agent") {
    for (const app of apps) {
      if (app.agentPartner === sourceName)
        targets.push({ name: app.name, tag: "primary" });
      if (app.secondaryAgent === sourceName)
        targets.push({ name: app.name, tag: "secondary" });
    }
    for (const comp of components) {
      if (comp.agentPartner === sourceName)
        targets.push({ name: comp.name, tag: "primary" });
    }
  } else if (active.kind === "app") {
    if (active.data.agentPartner)
      targets.push({ name: active.data.agentPartner, tag: "primary" });
    if (active.data.secondaryAgent)
      targets.push({ name: active.data.secondaryAgent, tag: "secondary" });
    for (const comp of components) {
      if (comp.consumedBy.includes(sourceName))
        targets.push({ name: comp.name, tag: "consumption" });
    }
  } else if (active.kind === "component") {
    if (active.data.agentPartner)
      targets.push({ name: active.data.agentPartner, tag: "primary" });
    for (const consumer of active.data.consumedBy) {
      targets.push({ name: consumer, tag: "consumption" });
    }
  }

  return targets;
}

export function getConnectedNames(active: ActiveNode): Set<string> {
  return new Set(getRelationTargets(active).map((t) => t.name));
}

function buildLitEdges(active: ActiveNode): readonly Edge[] {
  if (!active) return [];
  const sourceName = active.data.name;
  const source = nodePosByName(sourceName);
  if (!source) return [];

  const out: Edge[] = [];
  for (const t of getRelationTargets(active)) {
    const to = nodePosByName(t.name);
    if (!to) continue;
    out.push({
      from: source,
      to,
      key: `${sourceName}->${t.name}:${t.tag}`
    });
  }
  return out;
}

export function RelationshipLines({ active }: { active: ActiveNode }) {
  if (!active) return null;
  const edges = buildLitEdges(active);
  if (edges.length === 0) return null;

  return (
    <svg
      className="pointer-events-none absolute inset-0 h-full w-full"
      viewBox="0 0 100 100"
      style={{ overflow: "visible" }}
      aria-hidden="true"
    >
      {edges.map((edge, i) => {
        const d = bezierPath(edge.from, edge.to);
        const edgeDelay = `${i * STAGGER_MS}ms`;
        return (
          <g key={edge.key}>
            <path
              d={d}
              pathLength={100}
              fill="none"
              stroke={ORANGE}
              strokeOpacity={0.32}
              strokeWidth={2.2}
              strokeLinecap="round"
              className="platform-edge"
              style={{ animationDelay: edgeDelay }}
            />
            <path
              d={d}
              pathLength={100}
              fill="none"
              stroke={ORANGE}
              strokeOpacity={1}
              strokeWidth={0.85}
              strokeLinecap="round"
              className="platform-edge"
              style={{ animationDelay: edgeDelay }}
            />
          </g>
        );
      })}
    </svg>
  );
}
